'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useUserStore } from '../../hooks/useUser';
import { shouldEnableAds, shouldShowAdsOnPage } from '../../utilities/ads';
import { isDev } from '../../utilities/env';

/**
 * Enhanced AdSense Ad component with:
 * - Lazy loading via IntersectionObserver
 * - CLS protection with minimum heights
 * - Pro member exclusion logic
 * - Responsive design support
 */
export default function AdsenseAd({
  adSlot, // Should be provided from AD_SLOTS constant
  adFormat = 'auto',
  responsive = true,
  style = { display: 'block', minHeight: '90px' }, // Default min-height to prevent CLS
  className = '',
  adLabel = 'default-slot', // For analytics tracking
}) {
  const pathname = usePathname();
  const { isProMemberActive } = useUserStore();
  const showAds = shouldEnableAds(isProMemberActive) && shouldShowAdsOnPage(pathname);
  const adsenseId = process.env.NEXT_PUBLIC_ADSENSE_ID || 'ca-pub-3709556784139074';
  const adRef = useRef(null);

  useEffect(() => {
    // Don't render ads for Pro members
    if (!showAds || !adRef.current) return;

    // Use IntersectionObserver for lazy loading
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          try {
            // Push the ad when it enters viewport
            // Make sure we're not trying to push an ad that's already been pushed
            const adElement = adRef.current;
            if (adElement && !adElement.dataset.adPushed) {
              // Mark this ad as pushed to prevent multiple pushes
              adElement.dataset.adPushed = 'true';

              // Push the ad
              (window.adsbygoogle = window.adsbygoogle || []).push({});

              // Track impression in GTM/GA4
              if (window.dataLayer) {
                window.dataLayer.push({
                  event: 'ad_impression',
                  ad_slot: adLabel,
                  ad_location: pathname,
                });
              } else if (window.gtag) {
                window.gtag('event', 'ad_impression', {
                  ad_slot: adLabel,
                  ad_location: pathname,
                });
              }
            }
          } catch (e) {
            console.error('AdSense error:', e);
          }

          // Disconnect after loading
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Start loading when 10% of the ad is visible
    );

    observer.observe(adRef.current);

    // Cleanup observer on unmount or route change
    return () => observer.disconnect();
  }, [pathname, showAds, adLabel, adsenseId]);

  // Don't render the ad slot at all for pro members
  if (!showAds) {
    return null;
  }

  return (
    <ins
      ref={adRef}
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={adsenseId}
      data-ad-slot={adSlot}
      data-ad-format={adFormat}
      data-full-width-responsive={responsive ? 'true' : 'false'}
      {...(isDev ? { 'data-adtest': 'on' } : {})}
    />
  );
}
